import "./styles.css";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { DriverShell } from "@carry/driver-ui";
import { createLIFFPlatform, createMockPlatform } from "@carry/messenger-platform/liff";

const BOOTSTRAP_TIMEOUT_MS = 8000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("LIFF_TIMEOUT")), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

function renderError(root: HTMLElement, message: string) {
  root.innerHTML = "";
  const wrap = document.createElement("div");
  wrap.setAttribute("role", "alert");
  wrap.style.cssText =
    "min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;padding:24px;font-family:system-ui;text-align:center;color:#333;";
  const title = document.createElement("h1");
  title.textContent = "LINE への接続に失敗しました";
  title.style.cssText = "font-size:20px;font-weight:600;margin:0;";
  const detail = document.createElement("p");
  detail.textContent = message;
  detail.style.cssText = "font-size:16px;line-height:1.6;color:#666;max-width:320px;margin:0;";
  const button = document.createElement("button");
  button.type = "button";
  button.textContent = "再読み込み";
  button.style.cssText =
    "background:#06c755;color:#fff;border:0;padding:12px 20px;border-radius:6px;font-size:16px;font-weight:600;cursor:pointer;min-height:48px;";
  button.addEventListener("click", () => window.location.reload());
  wrap.append(title, detail, button);
  root.append(wrap);
}

async function bootstrap() {
  const root = document.getElementById("root");
  if (!root) throw new Error("root element missing");

  try {
    // E2E モード: LINE SDK を呼ばずモック platform を使う (liff.login リダイレクト回避)。
    // セッションは Playwright が localStorage に注入するため DriverShell は即 active になる。
    const e2eMock = import.meta.env.VITE_E2E_DRIVER === "1";
    let platform;
    if (e2eMock) {
      platform = createMockPlatform();
    } else {
      const liffId = import.meta.env.VITE_DRIVER_LINE_APP_ID?.trim();
      if (!liffId) {
        throw new Error("VITE_DRIVER_LINE_APP_ID is not set");
      }
      platform = await withTimeout(createLIFFPlatform({ liffId }), BOOTSTRAP_TIMEOUT_MS);
    }

    // driver は自オリジン (driver.*) 経由で API をプロキシし、セッション Cookie を first-party 化する
    // (LIFF 埋め込みブラウザはクロスオリジン Cookie を遮断するため)。よって API は常に相対パス /api/* で呼ぶ。
    // 本番/ステージング/開発では driver Worker (worker.ts) が /api/* を API Worker へ転送し、
    // ローカル開発では Vite の server.proxy が /api を API (localhost:8080) へ転送する。
    // VITE_API_URL (cross-origin の絶対 URL) は driver では使わない。
    const apiBaseUrl = "";

    createRoot(root).render(
      <StrictMode>
        <DriverShell platform={platform} apiBaseUrl={apiBaseUrl} e2e={e2eMock} />
      </StrictMode>,
    );
  } catch (err) {
    const message =
      err instanceof Error && err.message === "LIFF_TIMEOUT"
        ? "8 秒以内に LINE と通信できませんでした。電波状況をご確認ください。"
        : err instanceof Error
          ? err.message
          : String(err);
    renderError(root, message);
  }
}

void bootstrap();
